import React, { useContext } from "react";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid";
import WorksiteNameColumn from "components/WorksiteNameColumn";
import CountryContext from "CountryContext";
import {FormattedMessage, IntlShape} from "react-intl";
import {NotificationsWithIdSchema, UsersMinimalSchema, WorksitesWithIdSchema} from "@feature/types";
import NotificationPing from "@ui-components/NotificationPing";
import {sharingInfoButton} from "@components/SharingInfoButton";
import type {Column} from "@material-table/core";
import Placeholder from "@ui-components/Placeholder";
import {CheckIcon, XMarkIcon} from "@heroicons/react/24/outline";


const getWorksiteNotifications = (
  notifications: NotificationsWithIdSchema[] | undefined,
  worksiteId: number,
) => {
  const worksiteNotifications = notifications
    ?.filter(({cod_worksite}) => cod_worksite === worksiteId)
  ?? [];
  return {
    isNew: worksiteNotifications.some(({val_type}) => val_type === "worksite"),
    hasNewContents: worksiteNotifications.some(({val_type}) => val_type !== "worksite"),
    hasTaskNotification: worksiteNotifications.some(({val_type}) => val_type === "worksite_tasks"),
    hasTaskActivityNotification: worksiteNotifications.some(({val_type}) => val_type === "worksite_tasks_activities"),
  }
}


export const getColumns = (
  intl: IntlShape,
  hideFlgTest: boolean,
  setShowSharing: React.Dispatch<React.SetStateAction<boolean>>,
  users: UsersMinimalSchema[] | undefined,
  notifications: NotificationsWithIdSchema[] | undefined,
): Column<WorksitesWithIdSchema>[] => [
  {
    field: "id",
    hidden: true,
    defaultSort: "desc",
  },
  {
    title: intl.formatMessage({ id: "name" }),
    field: "address",
    type: "string",
    headerStyle: { minWidth: 270 },
    cellStyle: { minWidth: 270, textTransform: "uppercase" },
    render: (rowData) => {
      const {
        country: { code },
      } = useContext(CountryContext);
      const {
        isNew,
        hasNewContents,
        hasTaskNotification,
        hasTaskActivityNotification
      } = getWorksiteNotifications(notifications, rowData.id);
      return (
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="flex gap-2.5 items-center">
            {hasNewContents && <NotificationPing />}
            <WorksiteNameColumn
              des_worksite={rowData.des_worksite}
              address={rowData.address}
              city={rowData.city}
              id={rowData.id}
              countryCode={code}
            />
          </div>
          <div className="flex gap-2">
            {isNew && (
              <span className="rounded-lg bg-red-500 text-white font-bold text-xs px-1.5 py-0.5">
                {intl.formatMessage({ id: "new" })}
              </span>
            )}
            {hasTaskNotification && (
              <span className="rounded-lg bg-red-500 text-white font-bold text-xs px-1.5 py-0.5">
                {intl.formatMessage({ id: "task_notification" })}
              </span>
            )}
            {hasTaskActivityNotification && (
              <span className="rounded-lg bg-red-500 text-white font-bold text-xs px-1.5 py-0.5">
                {intl.formatMessage({ id: "task_act_notification" })}
              </span>
            )}
          </div>
        </div>
      );
    },
  },
  {
    title: intl.formatMessage({ id: "worksite_city" }),
    field: "city",
    type: "string",
  },
  {
    title: intl.formatMessage({ id: "worksite_province" }),
    field: "des_province",
    type: "string",
  },
  {
    title: intl.formatMessage({ id: "worksite_type" }),
    field: "cod_worksite_type",
    type: "string",
    render: ({ cod_worksite_type }) => (
      <div className="font-semibold">
        {intl.formatMessage({ id: cod_worksite_type })}
      </div>
    ),
  },
  {
    title: intl.formatMessage({ id: "worksite_status" }),
    field: "cod_worksite_status",
    type: "string",
    render: ({ cod_worksite_status }) => {
      const current = Object.values(cod_worksite_status ?? {})
        .filter((entry) => entry.flg_active && !!entry.date)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .shift();
      if (!current) return "-";
      return <FormattedMessage id={current?.step} />
    },
    sorting: false,
  },
  {
    title: intl.formatMessage({ id: "worksite_creator" }),
    field: "creator",
    type: "string",
    headerStyle: { whiteSpace: "nowrap" },
    cellStyle: { whiteSpace: "nowrap", textTransform: "uppercase" },
    render: ({ id_creator }) => {
      let contents;
      if (!users) {
        contents = <>
          <Placeholder width="w-40" height="h-4" marginClass="" />
          <Placeholder width="w-16" height="h-2" marginClass="" />
        </>
      }
      else {
        const user = users?.find(({ id }) => id === id_creator);
        if (!user)
          contents = (
            <span className="text-gray-400">
              <FormattedMessage id="not_available" defaultMessage="Non disponibile" />
            </span>
          )
        else {
          contents = <>
            <span>{user.name}</span>
            <span className="text-xs italic">
              <FormattedMessage id={user.account_user_role_name || user.role_name}/>
            </span>
          </>
        }
      }
      return (
        <div className="flex flex-col gap-1">
          {contents}
        </div>
      )
    },
    sorting: false,
  },
  {
    title: intl.formatMessage({ id: "shared_with" }),
    field: "shared_with_ids",
    headerStyle: { whiteSpace: "nowrap" },
    render: ({ shared_with_ids }) => {
      const sharing = shared_with_ids
        ?.map((uid) => {
          const user = users?.find(({ id }) => id === uid);
          if (!user) return null;
          return {
            id: user.id,
            firstname: user.name,
            lastname: "",
            role: {name: user.role_name},
          }
        })
      return sharingInfoButton(sharing, setShowSharing, intl)
    },
    sorting: false,
  },
  {
    title: intl.formatMessage({ id: "worksite_istest" }),
    hidden: hideFlgTest,
    field: "flg_test",
    headerStyle: { whiteSpace: "nowrap"},
    render: ({ flg_test }) => {
      const Icon = flg_test ? CheckIcon : XMarkIcon;
      return (
        <div className="flex justify-center">
          <Icon className="w-5" />
        </div>
      )
    },
    type: "boolean",
  },
];

export const OPTIONS = {
  filtering: false,
  toolbar: false,
  sorting: true,
  emptyRowsWhenPaging: false,
  pageSizeOptions: [5, 10, 20, 50],
  headerStyle: {
    fontWeight: "bold",
    fontSize: 15,
    paddingTop: 8,
    paddingBottom: 8,
  },
  filterCellStyle: { paddingTop: 8, maxWidth: 200 },
  cellStyle: { paddingBottom: 5, textTransform: "uppercase" },
};

export const LOCALIZATION = {
  header: {
    actions: "",
  },
  pagination: {
    labelDisplayedRows: "{from}-{to} di {count} righe",
    labelRowsSelect: "Righe",
  },
};
